import CategoryLeftMenu from 'components/categories/CategoryLeftMenu'
import CategorySlider from 'components/categories/CategorySlider'
import Alerts from 'components/common/Alerts'
import LoadingOverlay from 'components/common/LoadingOverlay'
import ArrowUpIcon from 'components/icons/ArrowUpIcon'
import ProductCard from 'components/product/ProductCard'
import useCart from 'hooks/useCart'
import useMenus from 'hooks/useMenus'
import useSlug from 'hooks/useSlug'
import {
  isAbsent,
  isEmptyArray,
  isNonEmptyArray,
  isNonEmptyObj,
  isNonEmptyString,
  isPresent,
  slashSiteLinks,
} from 'lib/utils'
import dynamic from 'next/dynamic'
import { HomePageProps } from 'pages'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  ICategory,
  IProduct,
  selectBannersList,
  selectCategoryList,
  selectProductList,
  useGetPageQuery,
} from 'store/api'
import { selectIsBrowser } from 'store/ui'
import Head from 'next/head'
import WideAltCard from 'components/product/ProductCard/WideAltCard'
import { getSeoH1 } from 'config/seoH1'

// dynamic() без ssr:false → SSR HTML идентичен sync импорту (нет CLS),
// но код уходит в отдельный chunk → меньше main bundle → TBT падает.
const HomeBannerSlider = dynamic(() => import('./HomeBannerSlider'))
const HomeMobileBannerSlider = dynamic(() => import('./HomeMobileBannerSlider'))
const HomeCategoryMobileMenu = dynamic(() => import('./HomeCategoryMobileMenu'))

const ProductModal = dynamic(
  () => import('components/product/ProductCard/ProductModal'),
  { ssr: false }
)

const HomeView = ({
  seoPage,
  categoryListSSR,
  productListSSR,
  menus: menusSSR,
}: HomePageProps) => {
  const { queryLng } = useSlug()

  let [product, setProduct] = useState<IProduct | null>(null)
  let [isProductModalOpen, setIsProductModalOpen] = useState(false)

  let _products = useSelector(selectProductList(queryLng))
  let _categoryList = useSelector(selectCategoryList(queryLng))
  let isBrowser = useSelector(selectIsBrowser)

  let { leftMenu } = useMenus(menusSSR)

  let activeCategoryList = useMemo(
    () =>
      isNonEmptyArray(_categoryList)
        ? _categoryList
        : isBrowser
        ? []
        : categoryListSSR,
    [_categoryList, categoryListSSR, isBrowser]
  )

  let productList = useMemo(() => {
    return isPresent(_products) ? _products : productListSSR ?? {}
  }, [_products, productListSSR])

  const openProductModal = useCallback((product: IProduct) => {
    setProduct(product)
    setIsProductModalOpen(true)
  }, [])

  const closeProductModal = useCallback(() => {
    setIsProductModalOpen(false)
    setTimeout(() => {
      setProduct(null)
    }, 500)
  }, [])

  const banners = useSelector(selectBannersList(queryLng))?.banners
  const mobileBanners = useSelector(selectBannersList(queryLng))?.mobile?.banners

  // const isXl = useSelector(selectIsXl)

  const { items } = useCart()
  const firstCategory = activeCategoryList[0]
  const productsByFirstCategory = useMemo(() => {
    let _products = isPresent(firstCategory)
      ? productList[firstCategory.id]
      : []
    return _products
  }, [firstCategory, productList])

  const isAltMobileCategory =
    firstCategory?.id === 1 || firstCategory?.slug === 'novyij_god'

  const renderItem = useCallback(
    (p: IProduct) => {
      const _item = items.find((i) => i.product.id === p.id)
      const isSimpleProduct =
        (p.type === 'simple' || p.variations_count == 1) &&
        isAbsent(p.options?.ingsgroups)

      const quantity = isPresent(_item)
        ? isSimpleProduct
          ? _item.quantity
          : items.reduce((count, i) => {
              if (i.product.id === p.id) count += i.quantity
              return count
            }, 0)
        : 0

      const CardComponent = isAltMobileCategory ? WideAltCard : ProductCard

      return (
        <CardComponent
          {...p}
          items={items}
          quantity={quantity}
          key={p.id}
          descClassName='font-light'
          cb={() => openProductModal(p)}
          categorySlug={firstCategory?.slug}
        />
      )
    },
    [openProductModal, firstCategory, items]
  )

  useEffect(() => {
    if (isAbsent(window)) return
    const androidAppInstallPrompt = (_: any) => {
      console.log('beforeinstallprompt')
    }
    window.addEventListener(
      'beforeinstallprompt',
      androidAppInstallPrompt
    )
    return () => {
      window.removeEventListener(
        'beforeinstallprompt',
        androidAppInstallPrompt
      )
    }
  }, [])

  let { data: _seoPage } = useGetPageQuery(
    { lng: queryLng, slug: 'main-seo' },
    { skip: isNonEmptyString(seoPage) }
  )

  return (
    <>
      <Head>
        <meta name='apple-itunes-app' content='app-id=6447418248' />
      </Head>

      <div>
        <Alerts type='page.glavnaya' />

          {isNonEmptyArray(mobileBanners) && (
            <div className='lg:hidden'>
              <HomeMobileBannerSlider banners={mobileBanners} />
            </div>
          )}

          {isNonEmptyArray(banners) && (
            <div className='hidden lg:block'>
              <HomeBannerSlider banners={banners} />
            </div>
          )}

        {/* H1 главной — под баннером (SEO-таблица 02.09.2026, config/seoH1.ts) */}
        <div className='custom-px'>
          <h1 className='pt-4 lg:pt-0 font-unbounded font-medium text-lg md:text-xl lg:text-2xl lg:leading-tight tracking-tight'>
            {getSeoH1('/')}
          </h1>
        </div>

        <div className='flex flex-row'>
          <div
            className='relative mt-5 w-full'
            // className='relative mt-5 w-full xl:max-w-[calc(100%-300px)]'
          >
            <HomeCategoryMobileMenu
              categoryList={activeCategoryList}
              leftMenu={leftMenu}
            />

            <div className='custom-plx2 relative w-full'>
              <div className='flex items-start pt-4 pr-0 relative lg:bg-lightGrey w-full rounded-2xl lg:pb-2 lg:mb-4'>
                <CategoryLeftMenu
                  main={true}
                  leftMenu={leftMenu}
                  activeCategoryList={activeCategoryList}
                />

                <div className='lg:mb-20 flex-shrink w-full lg:w-[calc(100%-16rem)]'>
                  {isNonEmptyArray(activeCategoryList) &&
                    isNonEmptyObj(productList) &&
                    isNonEmptyArray(productsByFirstCategory) && (
                      <>
                        <div className='lg:hidden'>
                          <div className='grid gap-1 grid-cols-2 sm:grid-cols-3 lg:mr-5 mb-5'>
                            {productsByFirstCategory.map(
                              (x: IProduct) => renderItem(x)
                            )}
                          </div>
                        </div>
                        <div className='hidden lg:block'>
                          {activeCategoryList.map((c: ICategory) => (
                            <CategorySlider
                              wide
                              key={c.id}
                              category={c}
                              products={productList}
                              openProductModal={openProductModal}
                            />
                          ))}
                        </div>
                        {(isNonEmptyString(seoPage) ||
                          isPresent(_seoPage)) && (
                          <div className='hidden lg:block'>
                            <div
                              itemProp='description'
                              className='text-base2 text-bodyGrey leading-snug font-light my-6 xl:max-w-[calc(100%-3rem)]'
                              dangerouslySetInnerHTML={{
                                __html: slashSiteLinks(
                                  isNonEmptyString(seoPage)
                                    ? seoPage
                                    : _seoPage!.text
                                ),
                              }}
                            ></div>
                          </div>
                        )}
                      </>
                    )}

                  {(isEmptyArray(activeCategoryList) ||
                    isEmptyArray(productList)) && (
                    <LoadingOverlay
                      className='h-5 w-5 mx-2.5 bg-accent'
                      open
                    />
                  )}
                </div>

                <ProductModal
                  isProductModalOpen={isProductModalOpen}
                  closeProductModal={closeProductModal}
                  product={product}
                />
              </div>
            </div>

            <div className='hidden lg:block absolute top-8 bottom-0 z-50 right-1 pointer-events-none'>
              <div className='h-full max-h-screen sticky top-2 pb-4 flex flex-col justify-end'>
                <button
                  type='button'
                  aria-label='Наверх'
                  className='w-[3.75rem] h-[3.75rem] group rounded-full p-4 bg-primary hover:bg-darkYellow pointer-events-auto'
                  onClick={() => window.scrollTo({ top: 0 })}
                >
                  <ArrowUpIcon className='w-full h-full text-dark' />
                </button>
              </div>
            </div>
          </div>
          {/* {isXl && <PromoPanel />} */}
        </div>
      </div>
    </>
  )
}

export default HomeView
